import { useEffect, useMemo, useRef, useState } from "react";
import rulesMarkdown from "../docs/hljuedc-new.md" with { type: "text" };
import organizationLogo from "./assets/images/logo.svg";
import heroAvif from "./assets/images/generated/art-ref.avif";
import heroFallback from "./assets/images/generated/art-ref-fallback.jpg";
import heroWebp from "./assets/images/generated/art-ref.webp";
import "./index.css";

type View = "home" | "rules";
type TransitionPhase = "idle" | "leaving" | "entering";
type SharpTone = "cyan" | "lime" | "dark";
type Article = { title: string; paragraphs: string[] };
type Chapter = { title: string; articles: Article[] };

const chapterPattern = /^第[一二三四五六七八九十百]+章\s+(.+)$/;
const articlePattern = /^(第[一二三四五六七八九十百]+条)\s+(.+)$/;
const signupUrl = "https://exam.hljuedc.cn/s/WPdDdhTI";
const recruitmentSteps = [
  {
    number: "01",
    title: "统一入门测试",
    summary: "基础电脑操作 + 电控嵌入式或计算机视觉",
    detail: "大一学生在新生军训结束后的第一周周末参加统一测试，完成基础电脑操作测试，并从电控嵌入式、计算机视觉两个兴趣方向中选择一项。两项测试均达到合格线后，可进入后续招生培训与实验室选拔。",
  },
  {
    number: "02",
    title: "焊接技能培训",
    summary: "面向通过测试的大一学生，完成安全与焊接实践",
    detail: "培训安排在入门测试结束后的下一周周末，内容包含焊接基础、安全操作规范与简单电路板焊接实践。培训仅面向通过入门测试的大一学生。",
  },
  {
    number: "03",
    title: "综合档案",
    summary: "记录测试成绩、培训过程与焊接评分",
    detail: "三个实验室共享报名学生的基本信息、测试成绩与培训记录。指导教师会对焊接成果进行评分，评分作为后续实验室分配与培养的参考依据，但不作为淘汰依据。",
  },
  {
    number: "04",
    title: "实验室培养",
    summary: "依据方向、容量与综合记录进入后续培养",
    detail: "各实验室依据自身培养方向与容量，结合学生的测试和培训记录确定后续安排，并将结果记录于共享名单。大二学生由各实验室独立面试，录取后执行相应实验室单独制定的培养方案。",
  },
];

function parseRules(markdown: string): Chapter[] {
  const chapters: Chapter[] = [];
  let chapter: Chapter | undefined;
  let article: Article | undefined;

  for (const line of markdown.split(/\r?\n/).map((item) => item.trim()).filter(Boolean).slice(1)) {
    if (line.match(chapterPattern)) {
      chapter = { title: line, articles: [] };
      chapters.push(chapter);
      article = undefined;
      continue;
    }
    const articleMatch = line.match(articlePattern);
    if (articleMatch && chapter) {
      const nextArticle: Article = { title: articleMatch[1]!, paragraphs: [articleMatch[2]!] };
      article = nextArticle;
      chapter.articles.push(nextArticle);
      continue;
    }
    if (article) article.paragraphs.push(line);
  }
  return chapters;
}

const chapters = parseRules(rulesMarkdown);

function OrganizationLogo({ className = "" }: { className?: string }) {
  return <span className={`logo-mark ${className}`} style={{ "--logo-source": `url(${organizationLogo})` } as React.CSSProperties} aria-hidden="true" />;
}

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>;
}

function MenuIcon({ open }: { open: boolean }) {
  return <span className={`menu-icon ${open ? "is-open" : ""}`} aria-hidden="true">{open ? "×" : "☰"}</span>;
}

function SignalCanvas({ variant = "cyan" }: { variant?: "cyan" | "lime" }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    type Particle = { x: number; y: number; vx: number; vy: number; radius: number; phase: number };
    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let animationFrame = 0;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const color = variant === "lime" ? "232,255,34" : "32,200,233";

    const reset = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(width * pixelRatio));
      canvas.height = Math.max(1, Math.round(height * pixelRatio));
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      const count = Math.max(22, Math.min(68, Math.round((width * height) / 23000)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - .5) * .22,
        vy: (Math.random() - .5) * .18,
        radius: .6 + Math.random() * 1.5,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (time = 0) => {
      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = "lighter";

      particles.forEach((particle, index) => {
        if (!reduceMotion) {
          particle.x += particle.vx;
          particle.y += particle.vy;
          if (particle.x < -20) particle.x = width + 20;
          if (particle.x > width + 20) particle.x = -20;
          if (particle.y < -20) particle.y = height + 20;
          if (particle.y > height + 20) particle.y = -20;
        }

        for (let targetIndex = index + 1; targetIndex < particles.length; targetIndex += 1) {
          const target = particles[targetIndex]!;
          const dx = particle.x - target.x;
          const dy = particle.y - target.y;
          const distance = Math.hypot(dx, dy);
          if (distance < 118) {
            context.beginPath();
            context.moveTo(particle.x, particle.y);
            context.lineTo(target.x, target.y);
            context.strokeStyle = `rgba(${color},${(1 - distance / 118) * .15})`;
            context.lineWidth = .7;
            context.stroke();
          }
        }

        const pulse = reduceMotion ? 1 : .75 + Math.sin(time * .0015 + particle.phase) * .25;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.radius * pulse, 0, Math.PI * 2);
        context.fillStyle = `rgba(${color},${.28 + pulse * .32})`;
        context.shadowColor = `rgba(${color},.7)`;
        context.shadowBlur = 8;
        context.fill();
        context.shadowBlur = 0;
      });

      if (!reduceMotion) animationFrame = window.requestAnimationFrame(draw);
    };

    reset();
    draw();
    const resizeObserver = new ResizeObserver(reset);
    resizeObserver.observe(canvas);
    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [variant]);

  return <canvas ref={canvasRef} className={`signal-canvas signal-canvas-${variant}`} aria-hidden="true" />;
}

function AppHeader({ view, onNavigate }: { view: View; onNavigate: (view: View, anchor?: string) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = (next: View, anchor?: string) => { setMenuOpen(false); onNavigate(next, anchor); };

  return (
    <header className="site-header">
      <button className="brand" onClick={() => navigate("home")} aria-label="返回首页">
        <OrganizationLogo className="brand-mark" />
        <span className="brand-copy"><b>HLJU EDC</b><small>ELECTRONICS · UNION</small></span>
      </button>
      <nav className={`desktop-nav ${menuOpen ? "is-open" : ""}`} aria-label="主导航">
        <button className={view === "home" ? "active" : ""} onClick={() => navigate("home")}><span>INDEX</span><small>首页</small></button>
        <button className={view === "rules" ? "active" : ""} onClick={() => navigate("rules")}><span>REGULATIONS</span><small>招生规章</small></button>
        <button onClick={() => navigate("home", "about")}><span>ABOUT</span><small>关于我们</small></button>
      </nav>
      <div className="header-meta" aria-hidden="true"><span>45.755°N</span><span>126.635°E</span></div>
      <button className="menu-button" onClick={() => setMenuOpen((value) => !value)} aria-label={menuOpen ? "收起导航" : "展开导航"} aria-expanded={menuOpen}><MenuIcon open={menuOpen} /></button>
    </header>
  );
}

function TechLabel({ children, tone = "cyan" }: { children: React.ReactNode; tone?: SharpTone }) {
  return <span className={`tech-label sharp-tag sharp-tag-${tone}`}><i /><span>{children}</span></span>;
}

function Home({ onOpenRules }: { onOpenRules: () => void }) {
  return (
    <main className="home-view page-enter">
      <section className="hero" aria-labelledby="hero-title">
        <picture className="hero-image" aria-hidden="true">
          <source srcSet={heroAvif} type="image/avif" />
          <source srcSet={heroWebp} type="image/webp" />
          <img src={heroFallback} alt="" decoding="async" fetchPriority="high" />
        </picture>
        <div className="hero-grid" aria-hidden="true" />
        <SignalCanvas />
        <div className="hero-scan" aria-hidden="true" />
        <div className="hero-rail" aria-hidden="true"><span>JOINT LAB NETWORK / ONLINE</span><span>2026 · HARBIN</span></div>
        <div className="hero-copy">
          <TechLabel>HEILONGJIANG UNIVERSITY</TechLabel>
          <p className="hero-kicker">ELECTRONICS SCIENCE & PRODUCTION UNION</p>
          <h1 id="hero-title"><span>电子科创</span><br />联合体</h1>
          <p className="hero-intro">连接三个实验室，汇聚电路、代码与视觉，<br />让每一个想法从测试走向实践。</p>
          <div className="hero-actions">
            <a className="primary-button signup-button" href={signupUrl} target="_blank" rel="noopener noreferrer"><span>立即报名<small>APPLY NOW</small></span><ArrowIcon /></a>
            <button className="primary-button regulations-button" onClick={onOpenRules}><span>阅读招生规章<small>READ REGULATIONS</small></span><ArrowIcon /></button>
            <a className="text-link" href="#about">了解联合体 <ArrowIcon /></a>
          </div>
        </div>
        <div className="hero-index" aria-hidden="true"><strong>00</strong><div><span>01 / 02</span><b>INDEX</b></div></div>
        <div className="hero-status" aria-hidden="true">
          <div className="signal-bars"><i /><i /><i /><i /><i /></div><span>SYSTEM READY</span><b>100<small>%</small></b>
        </div>
        <a className="scroll-cue" href="#about" aria-label="向下滚动"><span>SCROLL</span><i /></a>
      </section>

      <section className="about-section" id="about">
        <div className="section-serial" aria-hidden="true">01</div>
        <div className="section-heading reveal"><TechLabel tone="dark">ABOUT / 组织介绍</TechLabel><h2>三个实验室，<br /><em>汇入同一信号。</em></h2></div>
        <div className="about-copy reveal">
          <p>黑龙江大学电子科学生产联合体由 311、211、602 三个实验室协同组成，面向黑龙江大学在籍全日制本科生开展招生、训练与工程实践。</p>
          <p>三个实验室统一协调招生资源、共享报名与培养记录，并围绕电控嵌入式、计算机视觉及电子系统开展学习、项目与竞赛训练。</p>
        </div>
        <div className="stats-grid reveal">
          <div><strong>03</strong><span>协同实验室<br />LABORATORIES</span></div>
          <div><strong>02</strong><span>入门方向<br />DIRECTIONS</span></div>
          <div><strong>24</strong><span>招生规程条款<br />ARTICLES</span></div>
        </div>
      </section>

      <section className="directions-section">
        <header className="section-bar reveal"><div><span>02</span><h2>TECH DIRECTIONS</h2></div><p>基础电脑操作 + 一项兴趣方向。</p></header>
        <div className="direction-grid">
          <article className="direction-card reveal">
            <span className="card-number">D-01</span><div className="circuit-icon embedded" aria-hidden="true"><i /><i /><i /><i /></div>
            <div><TechLabel>CONTROL / EMBEDDED</TechLabel><h3>电控嵌入式</h3><span>C 语言 · 基础电路 · 嵌入式系统</span></div>
          </article>
          <article className="direction-card vision reveal">
            <span className="card-number">D-02</span><div className="vision-icon" aria-hidden="true"><i /><i /></div>
            <div><TechLabel tone="dark">COMPUTER VISION</TechLabel><h3>计算机视觉</h3><span>图像处理 · Python · 计算机视觉</span></div>
          </article>
        </div>
      </section>

      <section className="process-section">
        <div className="process-heading reveal"><TechLabel>RECRUITMENT FLOW</TechLabel><h2>从测试到实验室培养</h2><p>以下为大一学生统一流程。大二学生由实验室独立面试，大三及以上原则上不予招收。</p></div>
        <ol className="process-list reveal">
          {recruitmentSteps.map(({ number, title, summary, detail }) => (
            <li key={number}>
              <details className="process-step">
                <summary><b>{number}</b><div><h3>{title}</h3><p>{summary}</p></div><span aria-hidden="true">+</span></summary>
                <div className="process-detail"><div><small>STEP INTRODUCTION</small><p>{detail}</p></div></div>
              </details>
            </li>
          ))}
        </ol>
        <div className="process-open-note reveal"><TechLabel tone="lime">OPEN LAB LEAGUE</TechLabel><p><strong>实验室联赛面向全体同学开放。</strong>联赛由联合小组及各实验室组织，无需通过入门测试、加入实验室或通过其他考核即可参与。</p></div>
        <div className="rules-callout reveal">
          <div><TechLabel tone="dark">DOCUMENT / HLJUEDC-NEW</TechLabel><h2>准备接收新的信号？</h2><p>先阅读完整招生工作规程，了解测试、培训、补考与实验室协同安排。</p></div>
          <button className="primary-button light" onClick={onOpenRules}><span>打开规章<small>OPEN DOCUMENT</small></span><ArrowIcon /></button>
        </div>
      </section>
    </main>
  );
}

function Rules({ onBack }: { onBack: () => void }) {
  const [activeChapter, setActiveChapter] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
      let current = 0;
      chapters.forEach((_, index) => { const node = document.getElementById(`chapter-${index}`); if (node && node.getBoundingClientRect().top < 210) current = index; });
      setActiveChapter(current);
    };
    onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const articleCount = useMemo(() => chapters.reduce((sum, chapter) => sum + chapter.articles.length, 0), []);
  const jumpTo = (index: number) => document.getElementById(`chapter-${index}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="rules-view page-enter">
      <div className="reading-progress" style={{ transform: `scaleX(${progress / 100})` }} />
      <section className="document-hero">
        <div className="document-map" aria-hidden="true" />
        <SignalCanvas variant="lime" />
        <div className="document-hero-copy"><button className="back-link" onClick={onBack}>← 返回首页 / BACK</button><TechLabel tone="lime">OFFICIAL DOCUMENT · 2026</TechLabel><h1>招生工作<br /><em>规程</em></h1><p>黑龙江大学电子科学生产联合体</p></div>
        <div className="document-code" aria-hidden="true"><OrganizationLogo /><span>DOC. HLJUEDC-NEW</span><b>REV / 01</b></div>
        <div className="document-stats"><div><strong>{String(chapters.length).padStart(2, "0")}</strong><span>章节<br />CHAPTERS</span></div><div><strong>{String(articleCount).padStart(2, "0")}</strong><span>条款<br />ARTICLES</span></div></div>
      </section>

      <section className="document-layout">
        <aside className="chapter-nav">
          <div className="chapter-nav-head"><span>CONTENTS</span><b>{String(activeChapter + 1).padStart(2, "0")} / {String(chapters.length).padStart(2, "0")}</b></div>
          <ol>{chapters.map((chapter, index) => <li key={chapter.title} className={activeChapter === index ? "active" : ""}><button onClick={() => jumpTo(index)}><b>{String(index + 1).padStart(2, "0")}</b><span>{chapter.title.replace(/^第.+章\s+/, "")}</span></button></li>)}</ol>
          <div className="chapter-progress"><i style={{ height: `${progress}%` }} /></div>
        </aside>

        <article className="document-content">
          <header className="content-lead"><TechLabel>PLEASE READ CAREFULLY</TechLabel><p>本文件为黑龙江大学电子科学生产联合体招生工作的执行依据，涵盖入门测试、培训、联赛参与及名单管理等环节。</p></header>
          {chapters.map((chapter, chapterIndex) => (
            <section className="rule-chapter" id={`chapter-${chapterIndex}`} key={chapter.title}>
              <header><span>{String(chapterIndex + 1).padStart(2, "0")}</span><div><small>CHAPTER {String(chapterIndex + 1).padStart(2, "0")}</small><h2>{chapter.title}</h2></div></header>
              <div className="articles">{chapter.articles.map((article) => <section className="rule-article" key={article.title}><h3>{article.title}</h3><div>{article.paragraphs.map((paragraph, index) => <p key={`${article.title}-${index}`}>{paragraph}</p>)}</div></section>)}</div>
            </section>
          ))}
          <footer className="document-footer"><OrganizationLogo /><div><b>END OF DOCUMENT</b><span>黑龙江大学电子设计科学生产联合体管理委员会负责解释</span></div><button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>返回顶部 ↑</button></footer>
        </article>
      </section>
    </main>
  );
}

export function App() {
  const [view, setView] = useState<View>(() => window.location.hash.startsWith("#rules") ? "rules" : "home");
  const [transitionPhase, setTransitionPhase] = useState<TransitionPhase>("idle");
  const [signupDocked, setSignupDocked] = useState(() => window.location.hash.startsWith("#rules"));
  const transitionTimer = useRef<number | undefined>(undefined);
  const transitionFrame = useRef<number | undefined>(undefined);
  const signupFrame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reveal = () => document.querySelectorAll<HTMLElement>(".reveal").forEach((element) => { if (element.getBoundingClientRect().top < window.innerHeight * .9) element.classList.add("is-visible"); });
    reveal(); window.addEventListener("scroll", reveal, { passive: true });
    return () => window.removeEventListener("scroll", reveal);
  }, [view]);

  useEffect(() => {
    if (view === "rules") {
      setSignupDocked(true);
      return;
    }

    setSignupDocked(false);
    const updateDockedState = () => {
      if (signupFrame.current) window.cancelAnimationFrame(signupFrame.current);
      signupFrame.current = window.requestAnimationFrame(() => {
        const hero = document.querySelector<HTMLElement>(".hero");
        setSignupDocked(Boolean(hero && hero.getBoundingClientRect().bottom <= Math.max(118, window.innerHeight * .16)));
      });
    };

    updateDockedState();
    window.addEventListener("scroll", updateDockedState, { passive: true });
    window.addEventListener("resize", updateDockedState);
    return () => {
      window.removeEventListener("scroll", updateDockedState);
      window.removeEventListener("resize", updateDockedState);
      if (signupFrame.current) window.cancelAnimationFrame(signupFrame.current);
    };
  }, [view]);

  const navigate = (next: View, anchor?: string, updateHistory = true) => {
    window.clearTimeout(transitionTimer.current);
    if (transitionFrame.current) window.cancelAnimationFrame(transitionFrame.current);

    const scrollToDestination = () => {
      if (anchor) document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo({ top: 0, behavior: "instant" });
    };

    if (next === view) {
      if (updateHistory) window.history.pushState(null, "", anchor ? `#${anchor}` : next === "rules" ? "#rules" : "#home");
      scrollToDestination();
      return;
    }

    setTransitionPhase("leaving");
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 280;
    transitionTimer.current = window.setTimeout(() => {
      if (updateHistory) window.history.pushState(null, "", anchor ? `#${anchor}` : next === "rules" ? "#rules" : "#home");
      setView(next);
      setTransitionPhase("entering");
      transitionFrame.current = window.requestAnimationFrame(() => {
        scrollToDestination();
        const enterDuration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 560;
        transitionTimer.current = window.setTimeout(() => setTransitionPhase("idle"), enterDuration);
      });
    }, delay);
  };

  useEffect(() => {
    const syncHistory = () => {
      const next = window.location.hash.startsWith("#rules") ? "rules" : "home";
      if (next !== view) navigate(next, undefined, false);
    };
    window.addEventListener("popstate", syncHistory);
    return () => window.removeEventListener("popstate", syncHistory);
  }, [view]);

  useEffect(() => () => {
    window.clearTimeout(transitionTimer.current);
    if (transitionFrame.current) window.cancelAnimationFrame(transitionFrame.current);
    if (signupFrame.current) window.cancelAnimationFrame(signupFrame.current);
  }, []);

  const floatingSignupVisible = signupDocked && transitionPhase === "idle";

  return <div className="app-shell"><AppHeader view={view} onNavigate={navigate} /><div className={`route-stage is-${transitionPhase}`} aria-busy={transitionPhase !== "idle"}>{view === "home" ? <Home onOpenRules={() => navigate("rules")} /> : <Rules onBack={() => navigate("home")} />}</div><a className={`floating-signup ${floatingSignupVisible ? "is-visible" : ""}`} href={signupUrl} target="_blank" rel="noopener noreferrer" aria-hidden={!floatingSignupVisible} tabIndex={floatingSignupVisible ? 0 : -1}><span><b>立即报名</b><small>APPLY NOW</small></span><ArrowIcon /></a></div>;
}

export default App;
